import { apiRequest, ApiError } from "@/lib/api/client";
import { getAuthToken } from "@/lib/api/token-store";

/**
 * REST client for admin-managed calendars (ADMIN_API.md § Calendars). Mirrors
 * src/lib/api/queues.js: every call is employee-gated server-side (scheduling
 * view/manage; direct entries need calendar:manage). Thin on purpose — shapes
 * are normalized by src/lib/calendars.js.
 */

function createAuthHeaders(token, headers = {}) {
  const normalizedToken = String(token || getAuthToken() || "").trim();
  if (!normalizedToken) {
    throw new ApiError("authorization token is required", 401);
  }
  return {
    Authorization: `Bearer ${normalizedToken}`,
    ...headers,
  };
}

function jsonHeaders(token) {
  return {
    ...createAuthHeaders(token),
    "Content-Type": "application/json",
  };
}

function requireCalendarId(calendarId) {
  const id = String(calendarId || "").trim();
  if (!id) {
    throw new ApiError("calendarId is required", 400);
  }
  return id;
}

function requireBody(body) {
  if (!body || typeof body !== "object" || Array.isArray(body)) {
    throw new ApiError("body is required", 400);
  }
  return body;
}

/** GET /api/admin/calendars — every non-deleted calendar. Returns an array of raw items. */
export async function fetchAdminCalendars({ token, includeUsage = true, q, doctorId, tenantId, status } = {}) {
  const query = new URLSearchParams();
  if (includeUsage) query.set("includeUsage", "true");
  if (q) query.set("q", String(q).trim());
  if (doctorId) query.set("doctorId", String(doctorId).trim());
  if (tenantId) query.set("tenantId", String(tenantId).trim());
  if (status) query.set("status", String(status).trim());
  const path = query.toString() ? `/api/admin/calendars?${query}` : "/api/admin/calendars";
  const res = await apiRequest(path, {
    method: "GET",
    headers: createAuthHeaders(token),
    cache: "no-store",
  });
  return Array.isArray(res?.items) ? res.items : [];
}

/** GET /api/admin/calendars/{id} — one calendar incl. usage. */
export async function fetchAdminCalendar({ token, calendarId } = {}) {
  const id = requireCalendarId(calendarId);
  return apiRequest(`/api/admin/calendars/${encodeURIComponent(id)}`, {
    method: "GET",
    headers: createAuthHeaders(token),
    cache: "no-store",
  });
}

/** POST /api/admin/calendars — create. `body` is a normalized calendar draft. */
export async function createAdminCalendar({ token, body } = {}) {
  return apiRequest("/api/admin/calendars", {
    method: "POST",
    headers: jsonHeaders(token),
    body: JSON.stringify(requireBody(body)),
  });
}

/** PUT /api/admin/calendars/{id} — partial update (see buildCalendarUpdateBody). */
export async function updateAdminCalendar({ token, calendarId, body } = {}) {
  const id = requireCalendarId(calendarId);
  return apiRequest(`/api/admin/calendars/${encodeURIComponent(id)}`, {
    method: "PUT",
    headers: jsonHeaders(token),
    body: JSON.stringify(requireBody(body)),
  });
}

/**
 * DELETE /api/admin/calendars/{id} — cascade delete. Resolves to
 * `{id, deleted, cascade: {slots, sessionBlocks, directEntriesCancelled, appointmentsRetained}}`
 * (or null when the server answers with an empty body).
 */
export async function deleteAdminCalendar({ token, calendarId } = {}) {
  const id = requireCalendarId(calendarId);
  return apiRequest(`/api/admin/calendars/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: createAuthHeaders(token),
  });
}

/**
 * POST /api/admin/calendars/{id}/entries — direct entry (no slot). `body` comes from
 * buildCalendarEntryBody. Resolves to `{success, appointment}`.
 */
export async function createCalendarEntry({ token, calendarId, body } = {}) {
  const id = requireCalendarId(calendarId);
  return apiRequest(`/api/admin/calendars/${encodeURIComponent(id)}/entries`, {
    method: "POST",
    headers: jsonHeaders(token),
    body: JSON.stringify(requireBody(body)),
  });
}
